import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Card
} from '@mui/material';
import { Search as SearchIcon, Person as PersonIcon } from '@mui/icons-material';
import api from '../../utils/api';

/**
 * Available Candidates = contacts with contact_type "candidate" that are not in any bench.
 * Used for Bench Sales to see pool of candidates that can be added to benches.
 */
const AvailableCandidates = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({ total: 0 });

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/bench-sales/candidates/available', {
        params: { page: page + 1, limit: rowsPerPage, search: search || undefined }
      });
      const data = response.data?.data || response.data;
      const list = data?.candidates || [];
      setCandidates(list);
      setPagination(data?.pagination || { total: list.length });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch candidates');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    const t = setTimeout(() => fetchCandidates(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchCandidates, search]);

  const contactTypeColors = {
    regular: '#6b7280',
    candidate: '#6366f1',
    employee: '#10b981',
    contractor: '#3b82f6',
    company: '#f59e0b'
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Available Candidates
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contacts with type &quot;Candidate&quot; that are not in any bench. Add them to a bench from Bench Details or from the bench list (+ icon).
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          placeholder="Search by name, email, skills..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 320 }}
        />
      </Paper>

      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Skills</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.map((c) => (
                    <TableRow
                      key={c.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon color="primary" fontSize="small" />
                          {c.first_name} {c.last_name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(c.contact_type || 'candidate').replace('_', ' ')}
                          size="small"
                          sx={{
                            bgcolor: `${contactTypeColors[c.contact_type] || '#6b7280'}20`,
                            color: contactTypeColors[c.contact_type] || '#6b7280'
                          }}
                        />
                      </TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.phone || c.mobile || '–'}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>{c.skills ? `${String(c.skills).slice(0, 60)}${String(c.skills).length > 60 ? '…' : ''}` : '–'}</TableCell>
                    </TableRow>
                  ))}
                  {candidates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" py={4}>
                          No candidates match this list right now. Candidates can appear on multiple benches unless an administrator has locked them to one bench.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={pagination.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default AvailableCandidates;
